import { bootstrapApplication } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { unregisterAllServiceWorkersAndCaches } from './app/services/startup-debug.util';

// Disable debug logging across the app
const noop = () => {};
console.log = noop;
console.warn = noop;

const NATIVE_SW_RELOAD_KEY = 'aforo_native_sw_purge_reload_done';

/**
 * Capacitor WebView loads from https://localhost with the same SW/caching model as a PWA.
 * Our cache-first sw.js can serve a stale index.html after an app update, pointing at removed
 * hashed main.*.js files → blank screen before Angular (and self-heal) ever run.
 * Debug builds use a separate package id / storage, so they often "work" while release stays bricked.
 */
async function purgeServiceWorkerForNativeWebView(): Promise<void> {
  if (!Capacitor.isNativePlatform() || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    if (sessionStorage.getItem(NATIVE_SW_RELOAD_KEY) === '1') {
      return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length === 0) {
      return;
    }

    const registrationCount = registrations.length;
    await unregisterAllServiceWorkersAndCaches();

    sessionStorage.setItem(NATIVE_SW_RELOAD_KEY, '1');
    window.location.reload();
  } catch {
    // If purge fails, continue — skipping registration below still limits new bad state.
  }
}

function preventIosDoubleTabZoom(): void {
  if (!Capacitor.isNativePlatform()) return;
  document.addEventListener('gesturestart', (e) => e.preventDefault());
  document.addEventListener('gesturechange', (e) => e.preventDefault());
  document.addEventListener('gestureend', (e) => e.preventDefault());

  let lastTouchEnd = 0;
  document.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTouchEnd <= 300) e.preventDefault();
    lastTouchEnd = now;
  }, { passive: false });
}

async function startApplication(): Promise<void> {
  preventIosDoubleTabZoom();
  await purgeServiceWorkerForNativeWebView();

  if ('serviceWorker' in navigator && !Capacitor.isNativePlatform()) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js')
        .catch((error) => {
          console.error('❌ Service Worker registration failed:', error);
        });
    });
  }

  try {
    await bootstrapApplication(AppComponent, appConfig);
  } catch (err) {
    console.error(err);
  }
}

void startApplication();
